import { Edges, Environment, Html, Line, useAnimations, useGLTF } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";
import { memo, Suspense, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import type {
  InventoryItem,
  Point3,
  Recommendation,
  StationState,
  WorkerState,
  WorkerId,
} from "../types";

const fixedCameraPosition: Point3 = [6, 4, 18];
const fixedCameraTarget: Point3 = [6, 4, 0];

const aislePolygon: Array<[number, number]> = [
  [0.5, 5.9],
  [4.3, 5.9],
  [4.6, 6.2],
  [4.6, 7.4],
  [6, 7.4],
  [6, 5.9],
  [6.3, 5.6],
  [10.7, 5.8],
  [10.4, 5.2],
  [10.4, 4.1],
  [10.7, 3.8],
  [11.4, 3.8],
  [11.4, 1.7],
  [1.5, 1.5],
  [1.5, 3.5],
  [1.2, 3.8],
  [0.5, 3.8],
];

const equipmentLabels = [
  { stationId: "center_prep_table", label: "Prep" },
  { stationId: "gas_range", label: "Gas Range" },
  { stationId: "deep_fryer", label: "Fryer" },
  { stationId: "convection_oven", label: "Oven" },
  { stationId: "microwave_station", label: "Microwave" },
  { stationId: "three_compartment_sink", label: "Wash" },
  { stationId: "dry_storage_shelf", label: "Storage" },
] as const;

function FixedTopDownCamera() {
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(...fixedCameraPosition);
    camera.up.set(0, 1, 0);
    camera.lookAt(new THREE.Vector3(...fixedCameraTarget));
    camera.updateProjectionMatrix();
  }, [camera]);

  return null;
}

function KitchenModel() {
  const gltf = useGLTF("/models/kitchen_twin.glb");
  const scene = useMemo(() => gltf.scene.clone(true), [gltf.scene]);

  useEffect(() => {
    scene.traverse((node) => {
      if (node.name.startsWith("zone-") || node.name.startsWith("walkway-")) {
        node.visible = false;
      }
      if (node instanceof THREE.Mesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });
  }, [scene]);

  return <primitive object={scene} />;
}

function Aisle({ focused }: { focused: boolean }) {
  const geometry = useMemo(() => {
    const shape = new THREE.Shape();
    aislePolygon.forEach(([x, y], index) => {
      if (index === 0) shape.moveTo(x, y);
      else shape.lineTo(x, y);
    });
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, []);

  return (
    <mesh geometry={geometry} position={[0, 0, 0.025]} renderOrder={2}>
      <meshBasicMaterial
        color={focused ? "#d9efff" : "#dce9ee"}
        opacity={focused ? 0.28 : 0.14}
        transparent
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

function PersonModel({
  worker,
  focused,
  muted,
  action,
}: {
  worker: WorkerState;
  focused: boolean;
  muted: boolean;
  action: string;
}) {
  const group = useRef<THREE.Group>(null);
  const gltf = useGLTF("/models/kitchen-worker.glb");
  const person = useMemo(() => {
    const cloned = cloneSkeleton(gltf.scene);
    cloned.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return;
      const material = (node.material as THREE.MeshStandardMaterial).clone();
      material.color.set(muted ? "#8d939b" : worker.color);
      material.roughness = 0.78;
      material.metalness = 0.02;
      material.transparent = muted;
      material.opacity = muted ? 0.72 : 1;
      node.material = material;
      node.castShadow = true;
    });
    return cloned;
  }, [gltf.scene, muted]);
  const { actions } = useAnimations(gltf.animations, person);

  useEffect(() => {
    const selectedAction =
      actions[focused ? "Human Armature|Working" : "Human Armature|Idle"] ??
      Object.values(actions)[0];
    selectedAction?.reset().fadeIn(0.25).play();
    return () => {
      selectedAction?.fadeOut(0.2);
    };
  }, [actions, focused]);

  useFrame((_, delta) => {
    if (!group.current) return;
    const [x, y, z] = worker.targetPosition;
    group.current.position.lerp(new THREE.Vector3(x, y, z), Math.min(1, delta * 1.8));
  });

  return (
    <group
      ref={group}
      position={worker.position}
      rotation={[0, 0, Math.PI]}
      scale={0.310733}
    >
      <primitive object={person} />
      <mesh position={[0, 0, 0.035]}>
        <ringGeometry args={[0.38, 0.47, 48]} />
        <meshBasicMaterial
          color={focused ? worker.color : "#aab0b8"}
          opacity={focused ? 0.95 : 0.28}
          transparent
          depthWrite={false}
        />
      </mesh>
      <Html
        position={[0, 0, 5.25]}
        center
        distanceFactor={10}
        zIndexRange={[30, 1]}
      >
        <div className={`worker-map-label${focused ? " selected" : " muted"}`}>
          <strong>{worker.name}</strong>
          <span>{worker.role}</span>
          <em>{action}</em>
        </div>
      </Html>
    </group>
  );
}

const assetPaths = {
  carrot: "/models/carrot.glb",
  cabbage: "/models/cabbage.glb",
  noodles: "/models/noodle-basket.glb",
  bowl: "/models/dish-bowl.glb",
  wok: "/models/wok.glb",
  knife: "/models/chef-knife.glb",
  board: "/models/cutting-board.glb",
};

function StaticAsset({
  asset,
  position,
  scale = 1,
  rotation = [Math.PI / 2, 0, 0],
}: {
  asset: keyof typeof assetPaths;
  position: Point3;
  scale?: number;
  rotation?: Point3;
}) {
  const gltf = useGLTF(assetPaths[asset]);
  const object = useMemo(() => gltf.scene.clone(true), [gltf.scene]);
  return <primitive object={object} position={position} scale={scale} rotation={rotation} />;
}

function InventoryAssets({ items }: { items: InventoryItem[] }) {
  return (
    <>
      {items.map((item, index) => (
        <StaticAsset
          key={item.id}
          asset={item.asset}
          position={[
            item.position[0] + (index % 2) * 0.18,
            item.position[1] + Math.floor(index / 2) * 0.18,
            item.position[2],
          ]}
          scale={
            item.asset === "carrot"
              ? 0.35
              : item.asset === "cabbage"
                ? 0.7
                : 0.55
          }
        />
      ))}
    </>
  );
}

function DishFlow() {
  const bowl = useGLTF("/models/dish-bowl.glb");
  const root = useRef<THREE.Group>(null);
  const object = useMemo(() => bowl.scene.clone(true), [bowl.scene]);
  const points = useMemo(
    () =>
      new THREE.CatmullRomCurve3([
        new THREE.Vector3(6.7, 1.6, 1.05),
        new THREE.Vector3(5.5, 2.25, 1.05),
        new THREE.Vector3(4.2, 3.6, 1.05),
        new THREE.Vector3(3.7, 4.65, 1.05),
        new THREE.Vector3(2.15, 6.45, 1.05),
      ]),
    [],
  );
  useFrame(({ clock }) => {
    const progress = (clock.getElapsedTime() % 14) / 14;
    root.current?.position.copy(points.getPointAt(progress));
  });
  return (
    <group ref={root} scale={0.48} rotation={[Math.PI / 2, 0, 0]}>
      <primitive object={object} />
    </group>
  );
}

function RouteArrow({ route, color = "#3182f6" }: { route: Point3[]; color?: string }) {
  const marker = useRef<THREE.Mesh>(null);
  const curve = useMemo(
    () => new THREE.CatmullRomCurve3(route.map((point) => new THREE.Vector3(...point))),
    [route],
  );
  const linePoints = useMemo(() => curve.getPoints(80), [curve]);
  const arrowGeometry = useMemo(() => {
    const shape = new THREE.Shape();
    shape.moveTo(0, 0.34);
    shape.lineTo(-0.23, -0.22);
    shape.lineTo(0.23, -0.22);
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, []);
  const arrowPlacement = useMemo(() => {
    const end = route[route.length - 1];
    const previous = route[route.length - 2];
    return {
      position: [end[0], end[1], end[2] + 0.015] as Point3,
      angle: Math.atan2(end[1] - previous[1], end[0] - previous[0]) - Math.PI / 2,
    };
  }, [route]);
  useFrame(({ clock }) => {
    marker.current?.position.copy(curve.getPointAt((clock.getElapsedTime() * 0.16) % 1));
  });
  return (
    <group>
      <Line
        points={linePoints}
        color={color}
        lineWidth={4.5}
        dashed
        dashScale={1}
        dashSize={0.34}
        gapSize={0.22}
        transparent
        opacity={0.82}
      />
      <mesh ref={marker}>
        <sphereGeometry args={[0.13, 24, 24]} />
        <meshBasicMaterial color={color} />
      </mesh>
      <mesh
        geometry={arrowGeometry}
        position={arrowPlacement.position}
        rotation={[0, 0, arrowPlacement.angle]}
      >
        <meshBasicMaterial color={color} transparent opacity={0.9} depthWrite={false} />
      </mesh>
    </group>
  );
}

function routeForWorker(worker: WorkerState, station?: StationState): Point3[] {
  const start: Point3 = [worker.position[0], worker.position[1], 1.36];
  const hasDistinctTarget =
    Math.hypot(
      worker.targetPosition[0] - worker.position[0],
      worker.targetPosition[1] - worker.position[1],
    ) > 0.35;
  const destination = hasDistinctTarget
    ? worker.targetPosition
    : station?.position ?? worker.targetPosition;
  const end: Point3 = [destination[0], destination[1], 1.36];
  const safeAisleY = Math.max(2.25, Math.min(4.65, Math.max(start[1], end[1]) - 0.55));

  return [
    start,
    [start[0], safeAisleY, 1.36],
    [end[0], safeAisleY, 1.36],
    end,
  ];
}

function EquipmentOverlay({
  station,
  label,
  selected,
}: {
  station: StationState;
  label: string;
  selected: boolean;
}) {
  const x = station.width / 2;
  const y = station.depth / 2;
  const points: Point3[] = [
    [-x, -y, 0],
    [x, -y, 0],
    [x, y, 0],
    [-x, y, 0],
    [-x, -y, 0],
  ];
  const statusColor =
    station.status === "busy"
      ? "#e94b3c"
      : station.status === "watch"
        ? "#f5a623"
        : "#34a878";
  const overlayColor = selected ? "#3182f6" : statusColor;

  return (
    <group position={[station.position[0], station.position[1], 1.32]}>
      <mesh renderOrder={14}>
        <boxGeometry args={[station.width, station.depth, 0.025]} />
        <meshBasicMaterial
          color={overlayColor}
          opacity={selected ? 0.16 : 0.09}
          transparent
          depthTest={false}
          depthWrite={false}
        />
      </mesh>
      <Line
        points={points}
        color={overlayColor}
        lineWidth={selected ? 2.7 : 1.5}
        transparent
        opacity={selected ? 0.95 : 0.72}
        depthTest={false}
        renderOrder={15}
      />
      <Html position={[0, 0, 0.08]} center zIndexRange={[18, 1]}>
        <span className={`equipment-map-label${selected ? " selected" : ""}`}>
          {label} · {station.load}%
        </span>
      </Html>
    </group>
  );
}

function PassOverlay({ selected }: { selected: boolean }) {
  const passStation: StationState = {
    id: "pass",
    label: "Pass",
    category: "pass",
    maxCapacity: null,
    possibleActions: [],
    queue: 0,
    etaSeconds: 0,
    load: 0,
    position: [2.15, 6.45, 0.05],
    width: 2.5,
    depth: 0.7,
    status: "normal",
  };
  return <EquipmentOverlay station={passStation} label="Pass" selected={selected} />;
}

function TargetStation({
  station,
  recommendation,
}: {
  station: StationState;
  recommendation: Recommendation;
}) {
  return (
    <group position={[station.position[0], station.position[1], 1.32]}>
      <mesh>
        <boxGeometry args={[station.width, station.depth, 0.08]} />
        <meshBasicMaterial color="#3182f6" transparent opacity={0.14} depthWrite={false} />
        <Edges color="#3182f6" linewidth={2.5} />
      </mesh>
      <Html position={[0, 0, 1.65]} center distanceFactor={10} zIndexRange={[20, 0]}>
        <div className="station-callout">
          <span>DESTINATION - {station.label}</span>
          <strong>{recommendation.ingredients.join(" + ")}</strong>
          <em>{recommendation.action}</em>
        </div>
      </Html>
    </group>
  );
}

function SceneContents({
  workers,
  stations,
  inventory,
  recommendation,
  selectedWorkerId,
  visibleStationIds,
}: {
  workers: WorkerState[];
  stations: StationState[];
  inventory: InventoryItem[];
  recommendation: Recommendation | null;
  selectedWorkerId: WorkerId;
  visibleStationIds: string[];
}) {
  const activeWorkerId = selectedWorkerId;
  const visibleStationIdSet = useMemo(
    () => new Set(visibleStationIds),
    [visibleStationIds],
  );
  const selectedWorker = workers.find((worker) => worker.id === activeWorkerId);
  const selectedWorkerStation = selectedWorker
    ? stations.find((station) => station.id === selectedWorker.station)
    : undefined;
  const targetStation = recommendation
    ? stations.find((station) => station.id === recommendation.stationId)
    : undefined;
  const selectedStationId = targetStation?.id ?? selectedWorkerStation?.id;
  const jiwoo = workers.find((worker) => worker.id === "B");
  const washStation = stations.find((station) => station.id === "three_compartment_sink");
  const jiwooWashRoute =
    jiwoo && washStation ? routeForWorker(jiwoo, washStation) : null;
  const selectedRoute =
    recommendation?.workerId === activeWorkerId
      ? recommendation.route.map(
          ([x, y]) => [x, y, 1.36] as Point3,
        )
      : selectedWorker && selectedWorker.id !== "B"
        ? routeForWorker(selectedWorker, selectedWorkerStation)
        : null;

  const workerAction = (worker: WorkerState) => {
    if (recommendation?.workerId === worker.id) return recommendation.action;
    if (worker.id === "B") return "Moving to Three-Compartment Sink";
    const station = stations.find((item) => item.id === worker.station);
    if (worker.status === "moving") return `Moving to ${station?.label ?? "station"}`;
    if (worker.status === "available") return `Ready at ${station?.label ?? "station"}`;
    return `Working at ${station?.label ?? "station"}`;
  };

  return (
    <>
      <color attach="background" args={["#edf0f4"]} />
      <ambientLight intensity={1.8} />
      <directionalLight
        castShadow
        intensity={3.1}
        position={[8, 4, 12]}
        shadow-mapSize={[2048, 2048]}
      />
      <Environment preset="city" />
      <KitchenModel />
      <Aisle focused={Boolean(recommendation)} />
      {stations.filter((station) => visibleStationIdSet.has(station.id)).map((station) => (
        <mesh key={station.id} position={station.position}>
          <boxGeometry args={[station.width, station.depth, 0.025]} />
          <meshBasicMaterial
            color={
              station.status === "busy"
                ? "#ff6b52"
                : station.status === "watch"
                  ? "#ffb84d"
                  : "#63c9a2"
            }
            opacity={recommendation ? 0.035 : 0.075}
            transparent
            depthWrite={false}
          />
        </mesh>
      ))}
      {workers.map((worker) => (
        <PersonModel
          key={worker.id}
          worker={worker}
          focused={worker.id === activeWorkerId}
          muted={Boolean(activeWorkerId && worker.id !== activeWorkerId)}
          action={workerAction(worker)}
        />
      ))}
      {stations.filter((station) => visibleStationIdSet.has(station.id)).map((station) => {
        const compactLabel = equipmentLabels.find(
          (item) => item.stationId === station.id,
        )?.label;
        return (
          <EquipmentOverlay
            key={station.id}
            station={station}
            label={compactLabel ?? station.label}
            selected={
              selectedStationId === station.id ||
              (!recommendation && station.id === "three_compartment_sink")
            }
          />
        );
      })}
      {selectedStationId === "pass" ? <PassOverlay selected /> : null}
      <InventoryAssets items={inventory} />
      <StaticAsset asset="wok" position={[6.77, 0.82, 1.08]} scale={0.55} />
      <StaticAsset asset="knife" position={[5.45, 3.35, 1.02]} scale={0.48} />
      <StaticAsset asset="board" position={[5.4, 3.35, 0.96]} scale={0.65} />
      <DishFlow />
      {jiwooWashRoute ? <RouteArrow route={jiwooWashRoute} color="#3976f6" /> : null}
      {selectedRoute ? <RouteArrow route={selectedRoute} /> : null}
      {recommendation && targetStation ? (
        <>
          <TargetStation station={targetStation} recommendation={recommendation} />
          <StaticAsset
            asset={recommendation.asset}
            position={[
              targetStation.position[0] + 0.38,
              targetStation.position[1],
              1.08,
            ]}
            scale={recommendation.asset === "carrot" ? 0.35 : 0.55}
          />
        </>
      ) : null}
      <FixedTopDownCamera />
    </>
  );
}

export const KitchenScene = memo(function KitchenScene({
  workers,
  stations,
  inventory,
  recommendation,
  selectedWorkerId,
  visibleStationIds,
}: {
  workers: WorkerState[];
  stations: StationState[];
  inventory: InventoryItem[];
  recommendation: Recommendation | null;
  selectedWorkerId: WorkerId;
  visibleStationIds: string[];
}) {
  return (
    <Canvas
      shadows
      camera={{
        position: fixedCameraPosition,
        fov: 38,
        near: 0.1,
        far: 100,
        up: [0, 1, 0],
      }}
      dpr={[1, 1.8]}
      gl={{ antialias: true, alpha: false, toneMapping: THREE.ACESFilmicToneMapping }}
    >
      <Suspense fallback={null}>
        <SceneContents
          workers={workers}
          stations={stations}
          inventory={inventory}
          recommendation={recommendation}
          selectedWorkerId={selectedWorkerId}
          visibleStationIds={visibleStationIds}
        />
      </Suspense>
    </Canvas>
  );
});

useGLTF.preload("/models/kitchen_twin.glb");
useGLTF.preload("/models/kitchen-worker.glb");
Object.values(assetPaths).forEach((path) => useGLTF.preload(path));
